import {
    GetMicrostoreColumnTemplateQuery,
    MicrostoreColumnTemplateUpdateInput,
    MicrostoreLocationTemplateCreateOrUpdateInput,
    MicrostoreShelfTemplateCreateOrUpdateInput,
    ProductArrangementProductFragmentFragment,
    ProductArrangementWithProductsFragmentFragment,
} from 'data/__generated__';

export type MicrostoreLocationTemplateFormValues = MicrostoreLocationTemplateCreateOrUpdateInput & {
    productArrangement?: ProductArrangementWithProductsFragmentFragment | null;
    product?: ProductArrangementProductFragmentFragment | null;
};
export type MicrostoreShelfTemplateFormValues = Omit<
    MicrostoreShelfTemplateCreateOrUpdateInput,
    'locationTemplateTable'
> & {
    locationTemplateTable: MicrostoreLocationTemplateFormValues[][];
};
export type MicrostoreColumnTemplateFormValues = Omit<MicrostoreColumnTemplateUpdateInput, 'shelfTemplates'> & {
    shelfTemplates: MicrostoreShelfTemplateFormValues[];
};

// Fragment values to form values

function mapMicrostoreLocationTemplateToMicrostoreLocationTemplateFormValues({
    microstoreLocationTemplate: {
        _id,
        productArrangement,
        alternativeProductArrangementIds,
        product,
        reassortmentType,
        stockMax,
        shouldOrderProductIfNotInSite,
        shouldSmoothProductTransition,
        allowProductRotation,
        trayMaxCapacity,
    },
}: {
    microstoreLocationTemplate: GetMicrostoreColumnTemplateQuery['microstoreColumnTemplateQuery']['shelfTemplates'][number]['locationTemplateTable'][number][number];
}): MicrostoreLocationTemplateFormValues {
    return {
        _id,
        productArrangement,
        alternativeProductArrangementIds,
        product,
        reassortmentType,
        stockMax,
        shouldOrderProductIfNotInSite,
        shouldSmoothProductTransition,
        allowProductRotation,
        trayMaxCapacity,
    };
}

function mapMicrostoreShelfTemplateToMicrostoreShelfTemplateFormValues({
    microstoreShelfTemplate: { _id, locationTemplateTable },
}: {
    microstoreShelfTemplate: GetMicrostoreColumnTemplateQuery['microstoreColumnTemplateQuery']['shelfTemplates'][number];
}): MicrostoreShelfTemplateFormValues {
    return {
        _id,
        locationTemplateTable: locationTemplateTable.map((locationTemplateRow) =>
            locationTemplateRow.map((locationTemplate) =>
                mapMicrostoreLocationTemplateToMicrostoreLocationTemplateFormValues({
                    microstoreLocationTemplate: locationTemplate,
                }),
            ),
        ),
    };
}

export function mapMicrostoreColumnTemplateToMicrostoreColumnTemplateFormValues({
    microstoreColumnTemplate: { _id, name, type, state, shelfTemplates },
}: {
    microstoreColumnTemplate: GetMicrostoreColumnTemplateQuery['microstoreColumnTemplateQuery'];
}): MicrostoreColumnTemplateFormValues {
    return {
        _id,
        name,
        type,
        state,
        shelfTemplates: shelfTemplates.map((microstoreShelfTemplate) =>
            mapMicrostoreShelfTemplateToMicrostoreShelfTemplateFormValues({ microstoreShelfTemplate }),
        ),
    };
}

// Form values to input values

function mapMicrostoreLocationTemplateFormValuesToMicrostoreLocationTemplateUpdateInput({
    microstoreLocationTemplateFormValues: {
        _id,
        productArrangement,
        alternativeProductArrangementIds,
        product,
        reassortmentType,
        stockMax,
        shouldOrderProductIfNotInSite,
        shouldSmoothProductTransition,
        allowProductRotation,
        trayMaxCapacity,
    },
}: {
    microstoreLocationTemplateFormValues: MicrostoreLocationTemplateFormValues;
}): MicrostoreLocationTemplateCreateOrUpdateInput {
    return {
        _id,
        productArrangementId: productArrangement?._id ?? null,
        alternativeProductArrangementIds,
        productId: product?._id ?? null,
        reassortmentType,
        stockMax,
        shouldOrderProductIfNotInSite,
        shouldSmoothProductTransition,
        allowProductRotation,
        trayMaxCapacity,
    };
}

function mapMicrostoreShelfTemplateFormValuesToMicrostoreShelfTemplateUpdateInput({
    microstoreShelfTemplateFormValues: { _id, locationTemplateTable },
}: {
    microstoreShelfTemplateFormValues: MicrostoreShelfTemplateFormValues;
}): MicrostoreShelfTemplateCreateOrUpdateInput {
    return {
        _id,
        locationTemplateTable: locationTemplateTable.map((locationTemplateRow) =>
            locationTemplateRow.map((locationTemplate) =>
                mapMicrostoreLocationTemplateFormValuesToMicrostoreLocationTemplateUpdateInput({
                    microstoreLocationTemplateFormValues: locationTemplate,
                }),
            ),
        ),
    };
}

export function mapMicrostoreColumnTemplateFormValuesToMicrostoreColumnTemplateUpdateInput({
    microstoreColumnTemplateFormValues: { _id, name, type, state, shelfTemplates },
}: {
    microstoreColumnTemplateFormValues: MicrostoreColumnTemplateFormValues;
}): MicrostoreColumnTemplateUpdateInput {
    return {
        _id,
        name,
        type,
        state,
        shelfTemplates: shelfTemplates.map((microstoreShelfTemplateFormValues) =>
            mapMicrostoreShelfTemplateFormValuesToMicrostoreShelfTemplateUpdateInput({
                microstoreShelfTemplateFormValues,
            }),
        ),
    };
}
